import { useLayoutEffect, useRef, useState } from 'react';

import { Tooltip, type TypeTooltipProps } from '@thefarmersfront/kitchen';

import type { CustomCellRendererProps } from 'ag-grid-react';

import styles from './TableCellRenderer.module.scss';

export type TypeTooltipTextCellParams = {
  placement?: TypeTooltipProps['placement'];
  tooltipMaxWidth?: TypeTooltipProps['maxWidth'];
};

export type TypeTooltipTextCellProps<TData, TValue> = TypeTooltipTextCellParams &
  CustomCellRendererProps<TData, TValue>;

const TooltipTextCell = <TData, TValue>({
  value,
  valueFormatted,
  placement,
  tooltipMaxWidth,
}: TypeTooltipTextCellProps<TData, TValue>) => {
  const text = valueFormatted ?? (value == null ? '' : String(value));

  const textRef = useRef<HTMLSpanElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  useLayoutEffect(() => {
    const element = textRef.current;
    if (!element) return;

    const checkTruncated = () => setIsTruncated(element.scrollWidth > element.clientWidth);

    checkTruncated();

    const observer = new ResizeObserver(checkTruncated);
    observer.observe(element);
    return () => observer.disconnect();
  }, [text]);

  if (!text || text === '-') return '-';

  return (
    <Tooltip
      className={styles['tooltip-text-cell']}
      description={text}
      placement={placement}
      maxWidth={tooltipMaxWidth}
      disabled={!isTruncated}
      portalTarget={document.body}
    >
      <span ref={textRef} className={styles['tooltip-text-cell-text']}>
        {text}
      </span>
    </Tooltip>
  );
};

export default TooltipTextCell;
